"use client";

import { Button, Quantity } from "@components/ngg";
import { CheckIcon } from "@ngg/icons";
import type { FormEvent } from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import type { Product, Variant } from "types/product";

import { cn } from "@/lib/utils";
import { useCartActions, useCartState } from "@app/_context";

export default function AddToCart({
  showQuantity,
  product,
  variant,
  buttonText,
  buttonTextAdded,
  keepAddedState,
  classNames,
}: {
  showQuantity?: boolean;
  product: Product;
  variant?: Variant;
  buttonText: string;
  buttonTextAdded: string;
  keepAddedState?: boolean;
  classNames?: {
    form?: string;
    quantityWrapper?: string;
    button?: string;
    buttonAdded?: string;
  };
}) {
  const [quantity, setQuantity] = useState(1);
  const { cart, loading } = useCartState();
  const { addCartItem } = useCartActions();
  const isInCart =
    cart?.items?.some((item) => item?.partNo.toString() === product.id) ||
    false;
  const [isAddedToCart, setIsAddedToCart] = useState(
    keepAddedState ? isInCart : false,
  );

  useEffect(() => {
    if (!keepAddedState) return;
    setIsAddedToCart(isInCart);
  }, [isInCart, keepAddedState]);

  const disabled = useMemo(() => {
    if (product?.hasVariants) return !variant;
    return false;
  }, [product, variant]);

  const handleSubmit = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();

      if (!variant) return;

      setIsAddedToCart(true);

      await addCartItem([
        {
          quantity,
          item: variant,
        },
      ]);

      if (!keepAddedState) {
        setTimeout(() => setIsAddedToCart(false), 2000);
      }
    },
    [addCartItem, quantity, variant, keepAddedState],
  );

  const handleDecrease = () => setQuantity((q) => Math.max(1, q - 1));
  const handleIncrease = () => setQuantity((q) => q + 1);

  return (
    <form action="" onSubmit={handleSubmit} className={classNames?.form}>
      {showQuantity && (
        <div
          className={cn("shrink-0 basis-[132px]", classNames?.quantityWrapper)}
        >
          <Quantity
            readOnly
            onDecrease={handleDecrease}
            onIncrease={handleIncrease}
            value={quantity}
            classNames={{
              wrapper: "h-10",
              input: "border-grey-150 border-x  text-base",
              decreaseButton:
                "action-focus outline-grey-300 -outline-offset-1 basis-10 flex justify-center items-center",
              increaseButton:
                "action-focus outline-grey-300 -outline-offset-1 basis-10 flex justify-center items-center",
            }}
          />
        </div>
      )}
      <Button
        type="submit"
        className={cn(
          classNames?.button,
          isAddedToCart ? classNames?.buttonAdded : "",
          isAddedToCart && "pointer-events-none",
        )}
        disabled={disabled}
        isLoading={loading.addCartItem}
        startIcon={isAddedToCart && <CheckIcon className="text-lg" />}
        fullWidth
      >
        {isAddedToCart ? buttonTextAdded : buttonText}
      </Button>
    </form>
  );
}
